/* The sections of the app, and who can see them.
 *
 * One definition, because there were two and they did not agree. The mock store
 * called Applications "apps", Suppliers "vendors" and Backup & DR "dr", while
 * the sidebar routes them at /applications, /suppliers and /backup-dr — so a
 * module switch could never have matched the section it named, whatever it was
 * set to. That is half of why turning one off did nothing.
 *
 * The id is the route, minus its slash, with the dashboard at '/' called
 * 'dashboard'. Not a decision so much as the removal of one: any other key
 * needs a mapping table, and a mapping table is the thing that just drifted.
 *
 * Provision-LedgerSite.ps1 creates the list; Seed-LedgerSettings.ps1 fills
 * it from this same set of ids, and tests/modules.test.mjs fails if the two ever
 * stop matching.
 */

import type { IModule, IRoleDefinition } from '../models';

/** Every section, in sidebar order. */
export const DEFAULT_MODULES: readonly IModule[] = [
  { id: 'dashboard', label: 'Dashboard', icon: 'ti-layout-dashboard', enabled: true, group: 'Overview' },
  { id: 'expiries', label: 'Expiries', icon: 'ti-calendar-exclamation', enabled: true, group: 'Overview' },

  { id: 'people', label: 'Accounts', icon: 'ti-users', enabled: true, group: 'Register' },
  { id: 'onboarding', label: 'Onboarding', icon: 'ti-user-plus', enabled: true, group: 'Register' },
  { id: 'groups', label: 'Groups', icon: 'ti-users-group', enabled: true, group: 'Register' },
  { id: 'devices', label: 'Devices', icon: 'ti-device-laptop', enabled: true, group: 'Register' },
  { id: 'infrastructure', label: 'Infrastructure', icon: 'ti-server-2', enabled: true, group: 'Register' },
  { id: 'printers', label: 'Printers', icon: 'ti-printer', enabled: true, group: 'Register' },
  { id: 'licences', label: 'Licences', icon: 'ti-license', enabled: true, group: 'Register' },
  { id: 'import', label: 'Import', icon: 'ti-upload', enabled: true, group: 'Register' },

  { id: 'locations', label: 'Locations', icon: 'ti-map-pin', enabled: true, group: 'Infrastructure' },
  { id: 'network', label: 'Network', icon: 'ti-network', enabled: true, group: 'Infrastructure' },
  { id: 'applications', label: 'Applications', icon: 'ti-apps', enabled: true, group: 'Infrastructure' },
  { id: 'suppliers', label: 'Suppliers', icon: 'ti-building-store', enabled: true, group: 'Infrastructure' },
  { id: 'domains', label: 'Domains & SSL', icon: 'ti-world', enabled: true, group: 'Infrastructure' },
  { id: 'backup-dr', label: 'Backup & DR', icon: 'ti-shield-bolt', enabled: true, group: 'Infrastructure' },

  { id: 'knowledge-base', label: 'Knowledge base', icon: 'ti-book-2', enabled: true, group: 'Documentation' },
  { id: 'procedures', label: 'Procedures', icon: 'ti-checklist', enabled: true, group: 'Documentation' },
  { id: 'credentials', label: 'Credentials', icon: 'ti-key', enabled: true, group: 'Documentation' },
  { id: 'audit', label: 'Audit trail', icon: 'ti-history', enabled: true, group: 'Documentation' },
  { id: 'pack', label: 'Offline pack', icon: 'ti-printer', enabled: true, group: 'Documentation' },
  { id: 'labels', label: 'Asset labels', icon: 'ti-qrcode', enabled: true, group: 'Documentation' },
];

/** Sections that stay in the sidebar whatever their switch says.
 *
 *  The dashboard is where the app opens, and hiding the three Settings screens
 *  removes the only way to turn anything back on — a switch able to disable its
 *  own switch is a trap rather than a setting. */
export const FIXED_MODULES: ReadonlySet<string> = new Set([
  'dashboard',
  'modules',
  'record-types',
  'roles',
  'notifications',
]);

/** The module a sidebar link belongs to. */
export function moduleOf(to: string): string {
  if (to === '/') return 'dashboard';
  /* Offboarding is the other half of the same screen, not a section of its own: the
     same data, the same Entra writes, so the same permission and the same switch. */
  if (to === '/offboarding') return 'onboarding';
  return to.slice(1);
}

const every = (access: 'none' | 'read' | 'write') =>
  Object.fromEntries(DEFAULT_MODULES.map((m) => [m.id, access]));

/* The four groups from §9. This matrix decides what to draw and is explicitly
   not the security boundary — that is the SharePoint permission on the list,
   set by the provisioning script. Where the two disagree, SharePoint wins and
   this is the thing that is wrong. */
export const DEFAULT_ROLES: readonly IRoleDefinition[] = [
  {
    role: 'admin',
    label: 'IT administrator',
    group: 'Blueprint-Admins',
    description: 'Full control, including Credentials, API keys and settings.',
    matrix: every('write'),
    special: { export: true, manageRecordTypes: true, manageApiKeys: true },
  },
  {
    role: 'manager',
    label: 'Depot manager',
    group: 'Blueprint-Managers',
    description: 'Contributes on Devices, custom records and checklist runs. Reads the rest.',
    matrix: {
      ...every('read'),
      devices: 'write',
      infrastructure: 'write',
      printers: 'write',
      procedures: 'write',
      /* Not read either. This screen writes Entra group membership, which is a
         directory change no depot manager should be able to make from here —
         and a read-only view of it would be a screen whose every control is
         disabled, which reads as broken rather than as deliberate. */
      onboarding: 'none',
      /* Not a matter of taste: Blueprint_Credentials breaks inheritance and this
         group is not on it, so 'read' here would draw an access the site does
         not actually grant. */
      credentials: 'none',
      import: 'none',
    },
    special: { export: true, manageRecordTypes: false, manageApiKeys: false },
  },
  {
    role: 'finance',
    label: 'Finance',
    group: 'Blueprint-Finance',
    description: 'Contributes on Suppliers. Reads Applications, Licences and Accounts.',
    matrix: {
      ...every('none'),
      dashboard: 'read',
      expiries: 'read',
      suppliers: 'write',
      applications: 'read',
      licences: 'read',
      people: 'read',
    },
    special: { export: true, manageRecordTypes: false, manageApiKeys: false },
  },
  {
    role: 'reader',
    label: 'Read only',
    group: 'Blueprint-Readers',
    description: 'The knowledge base and the offline pack. Everything else is closed.',
    matrix: {
      ...every('none'),
      dashboard: 'read',
      'knowledge-base': 'read',
      pack: 'read',
    },
    special: { export: false, manageRecordTypes: false, manageApiKeys: false },
  },
];
